import { Constants } from '@truckdown/systems';

import context from '@truckdown/components-systems';
import { NoteModel, INoteManager, SearchNotesModel } from '@truckdown/locations';
import { ILookupService, RegionModel } from '@truckdown/systems';
import { defineComponent, ref, Ref, computed, onMounted, watch } from 'vue';

interface IPageKey {
    lastModified: Date,
    lastId: string
}

interface IData {
    isLoading: Ref<boolean>,
    total: Ref<number>,
    notes: Ref<NoteModel[]>,
    regions: Ref<RegionModel[]>,
    showFilter: Ref<boolean>,

    pagerPages: Ref<IPageKey[]>,

    range: Ref<Date[]>,
    regionCode: Ref<string>,
    city: Ref<string>,

    includeAll: Ref<boolean>,
    includeRatings: Ref<boolean>,
    includePreferences: Ref<boolean>
}

const getData = function (props: any) {
    return {
        isLoading: ref<boolean>(true),
        total: ref<number>(props.count ?? 0),
        notes: ref<NoteModel[]>(props.results ?? []),
        regions: ref<RegionModel[]>([]),
        showFilter: ref<boolean>(false),

        pagerPages: ref<IPageKey[]>([]),

        range: ref<Date[]>([]),
        regionCode: ref<string>(''),
        city: ref<string>(''),
        includeAll: ref<boolean>(props.isAdmin ? false : true),
        includeRatings: ref<boolean>(false),
        includePreferences: ref<boolean>(false)
    } as IData;
}


const getSearchModel = function (data: IData, isAdmin: boolean) {
    var model = {
        limit: 15
    } as SearchNotesModel;

    if (!isAdmin) {
        model.accountId = context.user?.accountId;
        (model as any).processed = null;

        if (!context.isInRole(Constants.Roles.FleetUserRole) || !data.includeAll.value) {
            model.userId = context.user?.id;
        }
    } else {
        if (data.includeAll.value) {
            (model as any).processed = null;
        } else {
            model.processed = false;
        }
    }

    if (data.range.value.length > 0) {
        if (data.range.value[0] != null) {
            model.range = {
                gte: data.range.value[0]
            };
            if (data.range.value.length > 1 && data.range.value[1] != null) {
                model.range.lt = data.range.value[1];
            }
        }
    }


    if (data.includeRatings.value) {
        model.hideRatings = false;
    } else {
        model.hideRatings = true;
    }

    if (data.includePreferences.value) {
        model.hideNetworkPreferences = false;
    } else {
        model.hideNetworkPreferences = true;
    }

    if (data.regionCode.value.length > 0) {
        model.regionCode = data.regionCode.value;
        if (data.city.value.length > 0) {
            model.city = data.city.value;
        }
    }

    return model;
}

const runSearch = async function (model: SearchNotesModel) {
    var service = await context.getService<INoteManager>('INoteManager');
    console.log(model);
    return await service.search(model);
}

const getComputed = function (data: IData) {
    return {
        caRegions: computed(() => {
            return data.regions.value
                .filter((r) => {
                    return r.country == 'CA';
                });
        }),
        usRegions: computed(() => {
            return data.regions.value
                .filter((r) => {
                    return r.country == 'US';
                });
        }),
        showCity: computed(() => {
            return data.regionCode.value.length > 0;
        }),

        pagerText: computed(() => {
            if (data.total.value == 0) {
                return '';
            }
            var prevCount = data.pagerPages.value.length * 15;
            return 'Displaying ' + (prevCount + 1) + ' to ' + (prevCount + data.notes.value.length) + ' of ' + data.total.value;
        }),

        pagerEnablePrevious: computed(() => {
            return data.pagerPages.value.length > 0;
        }),

        pagerEnableNext: computed(() => {
            if (data.notes.value.length < 15) {
                return false;
            }
            return (data.pagerPages.value.length * 15) + data.notes.value.length < data.total.value;
        })
    };
}

const refresh = function (data: IData, isAdmin: boolean) {
    data.isLoading.value = true;
    let model = getSearchModel(data, isAdmin);

    if (data.pagerPages.value.length > 0) {
        let prevStart = data.pagerPages.value[data.pagerPages.value.length - 1];
        model.lastModified = prevStart.lastModified;
        model.lastId = prevStart.lastId;
    }

    runSearch(model)
        .then((res) => {
            data.total.value = res.count;
            data.notes.value = res.notes;
        })
        .catch((err) => {
            console.log('Error refreshing notes list.', err);
        })
        .finally(() => {
            data.isLoading.value = false;
        });
}

const getMethods = function (data: IData, isAdmin: boolean) {
    return {
        toggleFilter: function (ev: Event) {
            data.showFilter.value = !data.showFilter.value;
        },
        search: function (ev: Event) {
            data.isLoading.value = true;

            let model = getSearchModel(data, isAdmin);
            runSearch(model)
                .then((res) => {
                    data.pagerPages.value = [];
                    data.total.value = res.count;
                    data.notes.value = res.notes;
                })
                .catch((err) => {
                    console.log('Error searching notes.', err);
                })
                .finally(() => {
                    data.isLoading.value = false;
                });
        },
        pagerNext: function (ev: Event) {
            data.isLoading.value = true;
            let model = getSearchModel(data, isAdmin);
            let lastEntry = data.notes.value[data.notes.value.length - 1];
            var key = {
                lastModified: lastEntry.modified,
                lastId: lastEntry.id
            } as IPageKey;

            model.lastModified = key.lastModified;
            model.lastId = key.lastId;

            runSearch(model)
                .then((res) => {
                    data.pagerPages.value.push(key);
                    data.total.value = res.count;
                    data.notes.value = res.notes;
                })
                .catch((err) => {
                    console.log('Error going to next page.', err);
                })
                .finally(() => {
                    data.isLoading.value = false;
                });
        },
        pagerPrevious: function (ev: Event) {
            if (data.pagerPages.value.length <= 1) {
                this.search(ev);
            } else {
                data.isLoading.value = true;
                let model = getSearchModel(data, isAdmin);
                let prevStart = data.pagerPages.value[data.pagerPages.value.length - 2];
                model.lastModified = prevStart.lastModified;
                model.lastId = prevStart.lastId;

                runSearch(model)
                    .then((res) => {
                        data.pagerPages.value.pop();
                        data.total.value = res.count;
                        data.notes.value = res.notes;
                    })
                    .catch((err) => {
                        console.log('Error going to next page.', err);
                    })
                    .finally(() => {
                        data.isLoading.value = false;
                    });
            }
        },
        editNote: function (note: NoteModel) {
            //if filter filters on processed and does not match then refresh ...
            let model = getSearchModel(data, isAdmin);
            if (model.processed != null && model.processed != note.processed) {
                refresh(data, isAdmin);
            } else {
                let existing = data.notes.value.find(n => n.id == note.id);
                if (existing) {
                    let idx = data.notes.value.indexOf(existing);
                    if (idx !== -1) {
                        data.notes.value[idx] = note;
                    }
                }
            }
        },
        deleteNote: function (noteId: string) {
            refresh(data, isAdmin);
        }
    };
}

export default defineComponent({
    props: {
        isAdmin: Boolean,
        count: Number,
        results: {
            type: Array as () => NoteModel[],
            required: false
        }
    },
    setup: function (props) {
        let data = getData(props);
        let computed = getComputed(data);
        let methods = getMethods(data, props.isAdmin ?? false);

        onMounted(async () => {
            var lookup = await context.getService<ILookupService>('ILookupService');
            data.regions.value = await lookup.getRegions();
            data.isLoading.value = false;
        });


        return {
            ...data,
            ...computed,
            ...methods,
            isFleet: context.isInRole(Constants.Roles.FleetUserRole)
        };
    }
});
